import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PolicyLimitsEditor from "./PolicyLimitsEditor";
import { commonSaveDataOne, initCommonState } from "../../../../redux/slices/commonSlice";
import { payrollProcessPath } from "../../../../services/config";
import { fetchBranchData } from "../../../../redux/slices/branchSlice";

// ─── Color tokens (matches Reimbursement theme) ───────────────────────────────
const C = {
  bg:              "var(--pay-bg)",
  surface:         "var(--pay-surface)",
  surfaceSecondary:"var(--pay-surface-2)",
  border:          "var(--pay-border)",
  text:            "var(--pay-text)",
  muted:           "var(--pay-text-muted)",
  purple:          "#8660F8",
};

const s = {
  input: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "10px 16px",
    color: C.text,
    fontSize: 12,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 12px",
    color: C.text,
    fontSize: 12,
    outline: "none",
    appearance: "none",
    cursor: "pointer",
    width: "100%",
  },
  btnPrimary: {
    background: C.purple,
    border: "none",
    borderRadius: 8,
    padding: "8px 14px",
    color: "#fff",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 0 20px rgba(134,96,248,0.25)",
  },
  btnOutline: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 14px",
    color: C.text,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
};

// ─── PolicyDashboard Component ────────────────────────────────────────────────
const PolicyDashboard = ({ onClose, initialEmployee }) => {
  const dispatch    = useDispatch();
  const commonStore = useSelector(s => s.common);
  const branchList  = useSelector(s => s.branch?.brnGetData || []);
  const user        = JSON.parse(localStorage.getItem("user_data")) || {};

  const [search,       setSearch]       = useState("");
  const [designation,  setDesignation]  = useState("");
  const [branch,       setBranch]       = useState("");
  const [statusFilter, setStatus]       = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showPolicyEditor, setShowPolicyEditor] = useState(false);
  const [editorGroupKey,   setEditorGroupKey]   = useState(null);
  const [editorPolicyKey,  setEditorPolicyKey]  = useState("");

  // ── API state ──────────────────────────────────────────────────────────
  const [policyGroups, setPolicyGroups] = useState([]);
  const [savedPolicies, setSavedPolicies] = useState([]);
  const [groupSummary, setGroupSummary] = useState(null);
  const [recentChanges, setRecentChanges] = useState([]);
  const [loading,      setLoading]      = useState(false);

  const rawPolicyCheck = initialEmployee?.policyCheck ?? initialEmployee?.policy?.status ?? initialEmployee?.policy?.check;
  const normalizedPolicyCheck = typeof rawPolicyCheck === "string" ? rawPolicyCheck.toLowerCase() : rawPolicyCheck;
  const assignedPolicyName = initialEmployee?.policyLabel
    || initialEmployee?.policyName
    || initialEmployee?.assignedPolicy
    || initialEmployee?.assigned_policy
    || initialEmployee?.policy?.name
    || initialEmployee?.policy?.label
    || (normalizedPolicyCheck ? "Assigned policy" : "No assigned policy found");
  const selectedEmployeeId = initialEmployee?.employeeId || initialEmployee?.staffId || initialEmployee?.employee_id || "—";
  const selectedEmployeeName = initialEmployee?.staffName || initialEmployee?.employeeName || initialEmployee?.name || "Selected employee";
  const selectedPolicyStatus = normalizedPolicyCheck === "within"
    ? "Within policy limit"
    : normalizedPolicyCheck || "Policy status unknown";
  const selectedPolicyKey = initialEmployee?.policyKey
    || initialEmployee?.policy?.key
    || initialEmployee?.assignedPolicyKey
    || initialEmployee?.assigned_policy_key
    || initialEmployee?.policy_key
    || "";
  const selectedPolicyGroupKey = initialEmployee?.policyGroupKey
    || initialEmployee?.policy?.groupKey
    || initialEmployee?.policy?.group_key
    || initialEmployee?.assignedPolicyGroupKey
    || initialEmployee?.assigned_policy_group_key
    || null;
  const selectedPolicyCategory = initialEmployee?.category || initialEmployee?.expenseType || initialEmployee?.policyCategory || "—";
  const selectedPolicyBranch = initialEmployee?.branch || initialEmployee?.branchName || "—";

  // ── Fetch policy groups on mount ──────────────────────────────────────
  const loadGroups = useCallback(() => {
    setLoading(true);
    dispatch(commonSaveDataOne({
      path: payrollProcessPath,
      data: { action: "fetchPolicyGroups", userId: Number(user.user_id) },
    }));
  }, [dispatch, user.user_id]);

  useEffect(() => {
    loadGroups();
    if (user.user_id) dispatch(fetchBranchData());
  }, []); // eslint-disable-line

  // ── Handle fetchPolicyGroups response (commonSuccessOne) ──────────────
  useEffect(() => {
    if (commonStore.commonSuccessOne) {
      const d = commonStore.commonDataOne;
      if (Array.isArray(d?.groups)) {
        setPolicyGroups(d.groups);
        setSavedPolicies(Array.isArray(d.policies) ? d.policies : []);
        if (d.summary) setGroupSummary(d.summary);
        if (Array.isArray(d.recentChanges)) {
          setRecentChanges(d.recentChanges);
        } else if (Array.isArray(d.changeHistory)) {
          setRecentChanges(d.changeHistory);
        } else {
          setRecentChanges([]);
        }
      } else if (Array.isArray(d)) {
        setPolicyGroups(d);
        setSavedPolicies([]);
        setRecentChanges([]);
      }
      setLoading(false);
      dispatch(initCommonState());
    }
    if (commonStore.commonErrorOne) {
      toast.error(commonStore.commonMessageOne || "Failed to load policy groups");
      setLoading(false);
      dispatch(initCommonState());
    }
  }, [commonStore.commonSuccessOne, commonStore.commonErrorOne, commonStore.commonDataOne, dispatch]);

  const totalEmployees = policyGroups.reduce((s, g) => s + Number(g.count || 0), 0);
  const coverageFromApi = policyGroups.length
    ? (() => {
        const max = Math.max(...policyGroups.map(g => Number(g.count || 0)), 1);
        return policyGroups
          .filter(g => g.type !== "all")
          .slice(0, 5)
          .map((g, i) => ({
            label: g.label,
            count: Number(g.count || 0),
            pct: Math.round((Number(g.count || 0) / max) * 100),
            gradient: [
              `linear-gradient(90deg,${C.purple},#a78bfa)`,
              "linear-gradient(90deg,#3b82f6,#60a5fa)",
              "linear-gradient(90deg,#f59e0b,#fbbf24)",
              "linear-gradient(90deg,#22c55e,#4ade80)",
              "linear-gradient(90deg,#ec4899,#f472b6)",
            ][i % 5],
          }));
      })()
    : [];

  const closeEditorAndRefresh = () => {
    setShowPolicyEditor(false);
    setEditorGroupKey(null);
    setEditorPolicyKey("");
    loadGroups();
  };

  const openEditor = (groupKey = null, policyKey = "") => {
    setEditorGroupKey(groupKey);
    setEditorPolicyKey(policyKey);
    setShowPolicyEditor(true);
  };

  const resolveEmployeePolicyEditor = () => {
    if (selectedPolicyKey) {
      return { groupKey: selectedPolicyGroupKey, policyKey: selectedPolicyKey };
    }

    const policyName = assignedPolicyName?.trim();
    if (!policyName) {
      return { groupKey: selectedPolicyGroupKey, policyKey: "" };
    }

    const savedPolicy = savedPolicies.find(p => p.name === policyName || p.key === policyName || p.name?.toLowerCase() === policyName.toLowerCase());
    if (savedPolicy) {
      return { groupKey: null, policyKey: savedPolicy.key };
    }

    const groupPolicy = policyGroups.find(g => g.label === policyName || g.key === policyName || g.label?.toLowerCase() === policyName.toLowerCase());
    if (groupPolicy) {
      return { groupKey: groupPolicy.key, policyKey: "" };
    }

    return { groupKey: selectedPolicyGroupKey, policyKey: "" };
  };

  // ── Build display policies from saved policies + existing employee groups ──
  const savedPolicyRows = savedPolicies.map(g => ({
        name:       g.label,
        appliesTo:  g.appliesTo || "All Employees",
        cap:       "—",
        categories:"—",
        employees:  g.count ?? 0,
        status:     g.status || "Active",
        updated:   "—",
        key:        g.key,
        branch:     g.branch,
        monthlyLimit: g.monthlyLimit,
        categoriesCount: g.categoriesCount,
        updatedLabel: g.updated,
        isSavedPolicy: true,
      }));

  const groupPolicyRows = policyGroups.map(g => ({
          name: g.label,
          appliesTo: g.type === "all" ? "All Employees" : g.label,
          cap: "—",
          categories: "—",
          employees: g.count ?? 0,
          status: "Active",
          updated: "—",
          key: g.key,
          branch: "all",
          monthlyLimit: "",
          categoriesCount: 0,
          updatedLabel: "",
          isSavedPolicy: false,
        }));

  const displayPolicies = [...savedPolicyRows, ...groupPolicyRows];
  const activePolicyCount = displayPolicies.filter(p => p.status === "Active").length;

  const filtered = displayPolicies.filter(p => {
    const matchSearch      = !search      || p.name.toLowerCase().includes(search.toLowerCase());
    const matchDesignation = !designation || p.appliesTo.toLowerCase().includes(designation.toLowerCase());
    const matchStatus      = !statusFilter || p.status === statusFilter;
    const matchBranch      = !branch || p.branch === branch || p.branch === "all";
    const matchInactive    = showInactive  || p.status !== "Inactive";
    return matchSearch && matchDesignation && matchStatus && matchBranch && matchInactive;
  });

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css" />
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #050505; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
        .pd-row:hover { background: ${C.surfaceSecondary} !important; transition: background 0.15s; }
        .pd-btn-outline:hover { border-color: ${C.purple} !important; }
      `}</style>

      {showPolicyEditor ? (
        <PolicyLimitsEditor
          onBack={closeEditorAndRefresh}
          onSaved={closeEditorAndRefresh}
          policyGroups={policyGroups}
          initialPolicyKey={editorPolicyKey}
          initialGroupKey={editorGroupKey}
        />
      ) : (
      <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Outfit', sans-serif", color: C.text }}>

        {/* ── PAGE HEADER ── */}
        <section style={{ padding: "28px 32px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            {/* Back button */}
            <button onClick={onClose} style={{ ...s.btnOutline, padding: "10px 14px", marginTop: 2 }} title="Back to Reimbursement">
              <i className="fa-solid fa-arrow-left" style={{ fontSize: 12 }} />
            </button>
            <div>
              <h1 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: "0 0 6px" }}>Policy Limits Dashboard</h1>
              <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>Manage reimbursement eligibility rules by designation, branch and employee group.</p>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
            <button className="pd-btn-outline" style={{ ...s.btnOutline }}>
              <i className="fa-solid fa-file-import" style={{ fontSize: 13 }} /> Import Policies
            </button>
            <button className="pd-btn-outline" style={{ ...s.btnOutline }}>
              <i className="fa-solid fa-download" style={{ fontSize: 13 }} /> Export Report
            </button>
            <button type="button" onClick={() => openEditor(null, "")} style={{ ...s.btnPrimary }}>
              <i className="fa-solid fa-plus" style={{ fontSize: 13 }} /> Add Policy
            </button>
          </div>
        </section>

        <div style={{ padding: "24px 32px" }}>

          {initialEmployee && (
            <section style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 20, marginBottom: 28 }}>
              <div style={{ background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Selected Employee</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{selectedEmployeeName}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>ID: {selectedEmployeeId}</div>
                <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 10 }}>
                  <span style={{ fontSize: 11, color: C.text, background: C.surface, borderRadius: 10, padding: "6px 10px" }}>Category: {selectedPolicyCategory}</span>
                  <span style={{ fontSize: 11, color: C.text, background: C.surface, borderRadius: 10, padding: "6px 10px" }}>Branch: {selectedPolicyBranch}</span>
                </div>
              </div>
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Assigned Policy</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.purple }}>{assignedPolicyName}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{selectedPolicyStatus}</div>
                <button style={{ marginTop: 16, ...s.btnOutline, width: "fit-content" }} onClick={() => {
                  const resolved = resolveEmployeePolicyEditor();
                  setEditorGroupKey(resolved.groupKey);
                  setEditorPolicyKey(resolved.policyKey);
                  setShowPolicyEditor(true);
                }}>
                  <i className="fa-solid fa-arrow-right" style={{ fontSize: 11 }} /> View policy details
                </button>
              </div>
            </section>
          )}

          {/* ── KPI CARDS ── */}
          <section style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
            {[
              { icon: "fa-file-lines",   iconBg: `rgba(134,96,248,0.1)`, iconColor: C.purple,  label: "Total Policies",    value: displayPolicies.length || "—", sub: "All created policies"    },
              { icon: "fa-circle-check", iconBg: "rgba(34,197,94,0.1)",  iconColor: "#22c55e", label: "Active Policies",   value: activePolicyCount || "—",                             sub: "Currently in use"         },
              { icon: "fa-users",        iconBg: "rgba(59,130,246,0.1)", iconColor: "#3b82f6", label: "Employees Covered", value: groupSummary?.employeesCovered ?? (totalEmployees || "—"), sub: "Under active policies" },
              { icon: "fa-clock",        iconBg: "rgba(245,158,11,0.1)", iconColor: "#f59e0b", label: "Pending Review",    value: groupSummary?.pendingReview   ?? "—",                             sub: "Rules need attention"     },
            ].map((k, i) => (
              <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: k.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className={`fa-solid ${k.icon}`} style={{ color: k.iconColor, fontSize: 20 }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 13, color: C.muted, margin: "0 0 4px" }}>{k.label}</p>
                    <p style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: 0 }}>{k.value}</p>
                  </div>
                </div>
                <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{k.sub}</p>
              </div>
            ))}
          </section>

          {/* ── FILTER BAR ── */}
          <section style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px 20px", marginBottom: 28 }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 14, alignItems: "center" }}>
              <div style={{ position: "relative" }}>
                <i className="fa-solid fa-search" style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 12 }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search policy name..."
                  style={{ ...s.input, paddingLeft: 44 }} />
              </div>
              <select value={designation} onChange={e => setDesignation(e.target.value)} style={s.select}>
                <option value="">All Designations</option>
                <option value="Engineer">Engineer</option>
                <option value="Manager">Manager</option>
                <option value="Executive">Executive</option>
                <option value="Senior">Senior</option>
              </select>
              <select value={branch} onChange={e => setBranch(e.target.value)} style={s.select}>
                <option value="">All Branches</option>
                {(branchList || []).map(b => (
                  <option key={b.branchId} value={String(b.branchId)}>{b.branch_name}</option>
                ))}
              </select>
              <select value={statusFilter} onChange={e => setStatus(e.target.value)} style={s.select}>
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 14, padding: "10px 16px", cursor: "pointer" }}
                onClick={() => setShowInactive(p => !p)}>
                {/* Toggle */}
                <div style={{ width: 44, height: 24, borderRadius: 12, background: showInactive ? C.purple : C.surfaceSecondary, border: `1px solid ${showInactive ? C.purple : C.border}`, position: "relative", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: 3, left: showInactive ? 22 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                </div>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 500, whiteSpace: "nowrap" }}>Show Inactive</span>
              </div>
            </div>
          </section>

          {/* ── MAIN LAYOUT: TABLE + SIDEBAR ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 24 }}>

            {/* LEFT — Policy Table */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                <i className="fa-solid fa-list" style={{ color: C.purple }} />
                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.text, margin: 0 }}>All Reimbursement Policies</h3>
              </div>
              <div style={{ overflowX: "auto" }}>
                {/* Header */}
                <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1.2fr 0.9fr 1.2fr 0.8fr 0.8fr 0.8fr 1fr", padding: "12px 24px", background: "rgba(22,16,50,0.5)", borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  <div>Policy Name</div><div>Applies To</div><div>Monthly Cap</div>
                  <div>Categories</div><div>Employees</div><div>Status</div>
                  <div>Last Updated</div><div>Action</div>
                </div>
                {/* Rows */}
                {loading && (
                  <div style={{ padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>Loading policies…</div>
                )}
                {!loading && filtered.length === 0 && (
                  <div style={{ padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>No policies found</div>
                )}
                {!loading && filtered.map((p, i) => (
                  <div key={i} className="pd-row" style={{ display: "grid", gridTemplateColumns: "1.8fr 1.2fr 0.9fr 1.2fr 0.8fr 0.8fr 0.8fr 1fr", padding: "14px 24px", borderBottom: `1px solid ${C.border}`, alignItems: "center", opacity: p.status === "Inactive" ? 0.6 : 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{p.appliesTo}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{p.monthlyLimit ? `\u20B9${p.monthlyLimit}` : p.cap}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{p.categoriesCount ? `${p.categoriesCount} active` : p.categories}</div>
                    <div>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: `rgba(134,96,248,0.1)`, border: `1px solid rgba(134,96,248,0.3)`, borderRadius: 8, fontSize: 11, fontWeight: 600, color: C.purple }}>
                        <i className="fa-solid fa-users" style={{ fontSize: 11 }} /> {p.employees}
                      </span>
                    </div>
                    <div>
                      <span style={{ display: "inline-flex", padding: "4px 12px", background: p.status === "Active" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${p.status === "Active" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`, borderRadius: 8, fontSize: 11, fontWeight: 600, color: p.status === "Active" ? "#22c55e" : "#ef4444" }}>
                        {p.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted }}>{p.updatedLabel || p.updated}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => p.isSavedPolicy ? openEditor(null, p.key) : openEditor(p.key, "")}
                        style={{ padding: "5px 12px", background: p.status === "Active" ? `rgba(134,96,248,0.1)` : C.surfaceSecondary, border: `1px solid ${p.status === "Active" ? "rgba(134,96,248,0.3)" : C.border}`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: p.status === "Active" ? C.purple : C.muted, cursor: "pointer" }}
                      >
                        View
                      </button>
                      <button style={{ width: 32, height: 32, background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="fa-solid fa-ellipsis-vertical" style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  </div>
                ))}
                {/* Pagination */}
                <div style={{ padding: "20px 24px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>Showing {filtered.length} of {displayPolicies.length} policies</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {["chevron-left", "1", "2", "chevron-right"].map((b, i) => (
                      <button key={i} style={{ width: 36, height: 36, background: b === "1" ? C.purple : C.surfaceSecondary, border: `1px solid ${b === "1" ? C.purple : C.border}`, borderRadius: 8, color: b === "1" ? "#fff" : C.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: b === "1" ? 700 : 400 }}>
                        {b.startsWith("chevron") ? <i className={`fa-solid fa-${b}`} style={{ fontSize: 11 }} /> : b}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — Sidebar */}
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* Coverage Breakdown */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-chart-pie" style={{ color: C.purple }} /> Coverage Breakdown
                </h3>
                {coverageFromApi.map((c, i) => (
                  <div key={i} style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <span style={{ fontSize: 15, color: C.text, fontWeight: 500 }}>{c.label}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{c.count}</span>
                    </div>
                    <div style={{ width: "100%", background: C.surfaceSecondary, height: 8, borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${c.pct}%`, height: "100%", borderRadius: 4, background: c.gradient }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Policy Alerts */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ color: C.purple }} /> Policy Alerts
                </h3>
                {[
                  { icon: "fa-layer-group",  color: "#f59e0b", bg: "rgba(245,158,11,0.05)", border: "rgba(245,158,11,0.2)", title: "Overlapping Policies",  sub: "2 policies have conflicting rules" },
                  { icon: "fa-user-slash",   color: "#ef4444", bg: "rgba(239,68,68,0.05)",  border: "rgba(239,68,68,0.2)",  title: "Unassigned Employees",  sub: "3 employees without policy" },
                  { icon: "fa-link-slash",   color: "#f59e0b", bg: "rgba(245,158,11,0.05)", border: "rgba(245,158,11,0.2)", title: "Inactive Mapping",       sub: "1 inactive rule still mapped" },
                ].map((a, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: 12, background: a.bg, border: `1px solid ${a.border}`, borderRadius: 10, marginBottom: 10 }}>
                    <i className={`fa-solid ${a.icon}`} style={{ color: a.color, fontSize: 12, marginTop: 2 }} />
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: "0 0 4px" }}>{a.title}</p>
                      <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{a.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 20 }}>Quick Actions</h3>
                {[
                  { icon: "fa-plus",       label: "Add Policy",             primary: true,  onClick: () => openEditor(null, "") },
                  { icon: "fa-users-gear", label: "Bulk Assign",            primary: false, onClick: undefined },
                  { icon: "fa-ban",        label: "Deactivate Old Rules",   primary: false, onClick: undefined },
                  { icon: "fa-download",   label: "Download Matrix",        primary: false, onClick: undefined },
                ].map((a, i) => (
                  <button key={i} type="button" onClick={a.onClick} className={a.primary ? "" : "pd-btn-outline"} style={{ ...(a.primary ? s.btnPrimary : s.btnOutline), width: "100%", justifyContent: "center", marginBottom: 10 }}>
                    <i className={`fa-solid ${a.icon}`} style={{ fontSize: 13 }} /> {a.label}
                  </button>
                ))}
              </div>

              {/* Recent Changes */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-clock-rotate-left" style={{ color: C.purple }} /> Recent Changes
                </h3>
                {recentChanges.length > 0 ? (
                  recentChanges.map((r, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: r.iconBg || "rgba(134,96,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <i className={`fa-solid ${r.icon || "fa-clock"}`} style={{ color: r.iconColor || C.purple, fontSize: 12 }} />
                      </div>
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 500, color: C.text, margin: "0 0 4px" }}>{r.text || r.title || "Policy update"}</p>
                        <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{r.time || r.updatedAt || "Just now"}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: 14, borderRadius: 12, background: C.surfaceSecondary, color: C.muted, fontSize: 12 }}>
                    No recent policy changes available.
                  </div>
                )}
                <button style={{ width: "100%", background: "none", border: "none", fontSize: 13, color: C.purple, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
                  View All Changes →
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
      )}
    </>
  );
};

export default PolicyDashboard;
